export const CHUNK_RECOVERY_STORAGE_KEY = 'savida:chunk-recovery-attempted';

const RECOVERABLE_CHUNK_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'loading chunk',
  'chunkloaderror',
  'not a valid javascript mime type',
];

export const isRecoverableChunkError = (error: unknown): boolean => {
  const isErrorEvent = typeof ErrorEvent !== 'undefined' && error instanceof ErrorEvent;
  if (isErrorEvent) {
    try {
      if (!error.filename || new URL(error.filename, window.location.href).origin !== location.origin) {
        return false;
      }
    } catch {
      return false;
    }
  }

  const message = isErrorEvent
    ? error.message
    : error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const normalized = message.toLowerCase();

  if (RECOVERABLE_CHUNK_PATTERNS.some((pattern) => normalized.includes(pattern))) {
    return true;
  }

  return (
    isErrorEvent &&
    error.lineno === 1 &&
    error.colno === 1 &&
    new URL(error.filename, window.location.href).origin === window.location.origin &&
    normalized.includes("unexpected token '<'")
  );
};

const currentEntryScript = (): string | undefined =>
  document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;

export const recoverFromStaleChunkOnce = (
  error: unknown,
  reload: () => void = () => window.location.reload()
): boolean => {
  if (!isRecoverableChunkError(error)) {
    return false;
  }

  const entryScript = currentEntryScript();
  if (!entryScript) {
    return false;
  }

  try {
    if (window.sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY) === entryScript) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RECOVERY_STORAGE_KEY, entryScript);
  } catch {
    // Without a persistent loop guard, an automatic reload can loop forever.
    return false;
  }

  reload();
  return true;
};

export const installChunkPreloadErrorHandler = (
  reload: () => void = () => window.location.reload()
): void => {
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (recoverFromStaleChunkOnce(payload ?? event, reload)) {
      event.preventDefault();
    }
  });

  window.addEventListener('error', (event) => {
    if (recoverFromStaleChunkOnce(event, reload)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });
};
